import React, {
  Fragment,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { CancelToken, isCancel } from 'axios';
import format from 'date-fns/format';
import { Button, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';

import { BrandPrimarySpinningCircle } from '../LoadingCircle';
import ModalLayout from '../ModalLayout';
import PaginationFooter from '../PaginationFooter';
import MaintenanceLogsService from '../../Services/MaintenanceLogsService';

export default memo(ViewMaintenanceModal);

const initState = {
    count: 0,
    csvDownloadPending: false,
    error: '',
    logs: [],
    logsLoading: true,
    page: 1,
  },
  limit = 20;

function ViewMaintenanceModal(props) {
  const {
      assetNickname,
      gateway_id,
      gateway_reminder_instance_id,
      toggleModal,
    } = props,
    handleToggleModalClick = useCallback(() => {
      toggleModal();
    }, [toggleModal]),
    service = useMemo(() => new MaintenanceLogsService(), []),
    [state, updateState] = useState(initState),
    pages = useMemo(() => Math.ceil(state.count / limit), [state.count]);

  const handleCsvDownload = useCallback(async () => {
    try {
      updateState((prevState) => ({ ...prevState, csvDownloadPending: true }));
      const params = gateway_id
        ? { gateway_id }
        : gateway_reminder_instance_id
          ? { gateway_reminder_instance_id }
          : {};
      const csv = await service.downloadMaintenanceLogsCsv(params);

      const blob = new Blob([csv], { type: 'text/csv' }),
        formattedDate = format(new Date(), 'y-MM-dd_HH:mm:ss'),
        filename = `Maintenance_Logs-${assetNickname}-${formattedDate}.csv`,
        link = document.createElement('a'),
        url = URL.createObjectURL(blob);

      link.download = filename;
      link.href = url;
      link.style = 'display: none';

      document.body.appendChild(link);
      link.click();

      window.setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        updateState((prevState) => ({
          ...prevState,
          csvDownloadPending: false,
        }));
      });
    } catch (err) {
      if (isCancel(err)) return;
      const error = err.response?.data?.message ?? err.message;
      updateState((prevState) => ({
        ...prevState,
        error,
        csvDownloadPending: false,
      }));
    }
  }, [assetNickname, gateway_id, gateway_reminder_instance_id, service]);

  const handlePageChange = useCallback((selected) => {
    updateState((prevState) =>
      selected + 1 === prevState.page
        ? prevState
        : { ...prevState, logsLoading: true, logs: [], page: selected + 1 }
    );
  }, []);

  useEffect(() => {
    const source = CancelToken.source();

    async function fetchMaintenanceLogs() {
      try {
        const filterParam = gateway_id
          ? { gateway_id }
          : gateway_reminder_instance_id
            ? { gateway_reminder_instance_id }
            : {};
        const params = {
          ...filterParam,
          limit,
          page: state.page,
        };
        const { count, data, page } = await service.getMaintenanceLogs(
          params,
          source.token
        );

        updateState((prevState) => ({
          ...prevState,
          count,
          page,
          logs: data,
          logsLoading: false,
        }));
      } catch (err) {
        if (isCancel(err)) return;
        const error = err.response?.data?.message ?? err.message;
        updateState((prevState) => ({
          ...prevState,
          error,
          logsLoading: false,
        }));
      }
    }

    fetchMaintenanceLogs();
    return source.cancel;
  }, [gateway_id, gateway_reminder_instance_id, service, state.page]);

  return (
    <ModalLayout centered isOpen={props.isOpen} scrollable size="xl">
      <ModalHeader className="border-bottom-0" toggle={handleToggleModalClick}>
        View Maintenance History
        <Button
          className="ml-md-3 rounded-lg"
          color="outline-secondary"
          disabled={state.csvDownloadPending}
          onClick={handleCsvDownload}
          size="sm"
        >
          {state.csvDownloadPending ? (
            <BrandPrimarySpinningCircle size="sm" />
          ) : (
            'Download Maintenance History'
          )}
        </Button>
      </ModalHeader>
      <ModalBody className="container-fluid pt-0">
        <div className="row sticky-top pt-3 bg-white">
          <p className="col-3 mb-0 nine-tenth-text text-muted text-center">
            Maintenance Reminder
          </p>
          <p className="col-3 mb-0 nine-tenth-text text-muted text-center">
            Date Triggered
          </p>
          <p className="col-3 mb-0 nine-tenth-text text-muted text-center">
            Service Date
          </p>
          <p className="col-3 mb-0 nine-tenth-text text-muted text-center">
            Service Done By
          </p>
          <div className="col-12">
            <hr className="mt-2" style={{ borderTopWidth: '2px' }} />
          </div>
        </div>
        {state.logsLoading ? (
          <div className="h-100 w-100 d-flex align-items-center justify-content-center">
            <BrandPrimarySpinningCircle size="3x" />
          </div>
        ) : (
          state.logs.map((log) => {
            const { unit } =
              props.signalUnitMap.get(
                log.GatewayReminderInstance?.GtemplateReminder?.signal_id
              ) || {};
            return (
              <Fragment key={log._id}>
                <div className="row align-items-center">
                  <p className="col-3 mb-0 nine-tenth-text text-center text-break">
                    {log.GatewayReminderInstance?.GtemplateReminder?.name}
                  </p>
                  <div className="col-3 d-flex flex-column align-items-center">
                    {log.MaintenanceNotification ? (
                      <>
                        <p className="mb-0 text-center text-muted three-quarter-text">
                          {format(
                            new Date(log.MaintenanceNotification.updatedAt),
                            'LLLL do, y'
                          )}
                        </p>
                        <p className="mb-0 text-center text-success three-quarter-text">
                          Triggered at{' '}
                          {log.MaintenanceNotification.signal_value}{' '}
                          {unit ? `Total ${unit}` : ''}
                        </p>
                      </>
                    ) : (
                      <p className="mb-0 text-center text-muted three-quarter-text">
                        n/a
                      </p>
                    )}
                  </div>
                  <div className="col-3">
                    <p className="mb-0 text-center text-muted three-quarter-text">
                      {format(new Date(log.service_date), 'LLLL do, y')}
                    </p>
                    <p className="mb-0 text-center text-success three-quarter-text">
                      Serviced at {log.signal_value_when_serviced}{' '}
                      {unit ? `Total ${unit}` : ''}
                    </p>
                  </div>
                  <p className="col-3 mb-0 nine-tenth-text text-center text-muted text-break">
                    {log.contact_info}
                  </p>
                </div>
                <div className="row">
                  <div className="col-12">
                    <hr />
                  </div>
                </div>
              </Fragment>
            );
          })
        )}
      </ModalBody>
      <ModalFooter className="border-top-0 justify-content-center">
        <PaginationFooter
          handlePageChange={handlePageChange}
          page={state.page}
          pages={pages}
          rowsLength={state.logs?.length ?? 0}
          rowsLoading={state.logsLoading}
        />
      </ModalFooter>
    </ModalLayout>
  );
}

ViewMaintenanceModal.propTypes = {
  assetNickname: PropTypes.string,
  gateway_id: PropTypes.string,
  gateway_reminder_instance_id: PropTypes.string,
  isOpen: PropTypes.bool,
  signalUnitMap: PropTypes.object,
  toggleModal: PropTypes.func,
};
